import { resolveSerializedWalkRoot } from './resolve-serialized-walk-root';
import { computeSerializedOffsets } from './serialized-offsets';

/**
 * Given a DOM Range and the cluster's EditorJS-id → DocumentData-UUID
 * mapping, resolve the triplet that the reference-selection flow needs:
 * `{sourceBlockId, position, length}`.
 *
 * Returns `null` when the range can't be resolved — either no `.ce-block`
 * ancestor (the range is outside the editor) or the range escapes a
 * single block (offsets helper rejects it).
 *
 * Pure: takes only a Range + a read-only mapping. No DOM mutation, no
 * toasts, no dispatch. Callers (cluster onPickerOpen, top-bar button)
 * own their own messaging.
 */
export function resolvePickerSource(
  range: Range,
  idMapping: Map<string, string>,
): { sourceBlockId: string; position: number; length: number } | null {
  const startEl =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? (range.startContainer as HTMLElement)
      : range.startContainer.parentElement;
  const blockEl = startEl?.closest<HTMLElement>('.ce-block');
  if (!blockEl) return null;

  const editorBlockId = blockEl.getAttribute('data-id') ?? '';
  const sourceBlockId = idMapping.get(editorBlockId) ?? editorBlockId;

  const walkRoot = resolveSerializedWalkRoot(blockEl);
  const offsets = computeSerializedOffsets(walkRoot, range);
  if (!offsets) return null;

  return {
    sourceBlockId,
    position: offsets.position,
    length: offsets.length,
  };
}
